import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Utensils, Award } from "lucide-react";
import { Reveal, ScriptKicker, FilipinoSun } from "../components/Reveal";
import { BUSINESS, LOGO_URL } from "../lib/data";
import { usePageMeta } from "../hooks/usePageMeta";

export default function About() {
  usePageMeta({
    title: "About Ateng K Sarap | Family-Owned Filipino Caterer",
    description:
      "Meet the family behind Ateng K Sarap — bringing home-inspired, authentic Filipino cooking to catering tables across San Diego.",
    path: "/about",
  });
  return (
    <div data-testid="about-page" className="overflow-hidden">
      {/* Page hero */}
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24 bg-background">
        <div className="absolute top-20 right-10 text-accent/25 hidden lg:block"><FilipinoSun size={120} /></div>
        <div className="container-eds grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-8">
            <div className="overline flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> About Ateng K Sarap</div>
            <ScriptKicker className="block mt-3">Our family kitchen</ScriptKicker>
            <h1 className="mt-2 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium text-foreground leading-[0.98] tracking-tight">
              A Filipino <span className="italic text-secondary">welcome</span>,
              <br />
              every <span className="italic text-primary">single time</span>.
            </h1>
          </div>
          <div className="lg:col-span-4 text-muted-foreground text-base lg:text-lg leading-relaxed">
            {BUSINESS.hero}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-32 bg-card">
        <div className="container-eds grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5 relative">
            <div className="polaroid rotate-l1">
              <img
                src="/images/hero-food.webp"
                alt="Filipino feast"
                loading="lazy"
                decoding="async"
                className="w-full h-[520px] object-cover"
              />
              <div className="polaroid-caption">A boodle for the family</div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-accent flex items-center justify-center rotate-12 shadow-xl">
              <div className="text-center">
                <div className="font-display text-2xl font-bold text-accent-foreground leading-none">Since</div>
                <div className="font-display text-2xl font-bold text-accent-foreground">Day 1</div>
              </div>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7 space-y-5 text-muted-foreground text-base lg:text-lg leading-relaxed" delay={0.1}>
            <div className="overline">Our Story</div>
            <h2 className="font-display text-4xl lg:text-5xl font-medium text-foreground leading-tight">
              Our story begins <span className="italic text-secondary">around the table</span>.
            </h2>
            <p>
              Ateng K Sarap began as a family kitchen — the kind where lola
              would slow-simmer adobo while tita laid out lumpia on banana
              leaves, and everyone within smelling distance would magically
              appear at the table. We grew up understanding that food is more
              than nourishment in Filipino culture. It is bayanihan. It is
              fiesta. It is love made visible.
            </p>
            <p>
              When we brought our kitchen to San Diego, our mission was simple:
              every event we cater should taste like home, and every guest
              should feel like family. We use generations-old recipes, source
              the freshest ingredients we can find, and prepare each dish with
              the same care our nanay taught us.
            </p>
            <p>
              Whether you're hosting an intimate birthday for ten or a corporate
              gathering for two hundred, we tailor every menu to your budget,
              your guests, and the story of your celebration.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="container-eds">
          <Reveal>
            <div className="text-center mx-auto max-w-3xl">
              <div className="overline justify-center flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> What We Stand For <span className="w-8 h-px bg-secondary" /></div>
              <ScriptKicker className="block mt-3">The flavors of family</ScriptKicker>
              <h2 className="mt-2 font-display text-4xl lg:text-5xl xl:text-6xl font-medium text-foreground leading-tight">
                The values we <span className="italic text-primary">cook with</span>.
              </h2>
            </div>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, bgClass: "bg-secondary text-secondary-foreground", title: "Made with Love", body: "Every dish carries the warmth of family recipes passed through generations." },
              { icon: Users, bgClass: "bg-primary text-primary-foreground", title: "Family First", body: "We treat every guest like a kasambahay — with care, generosity, and respect." },
              { icon: Utensils, bgClass: "bg-accent text-accent-foreground", title: "Authentic Flavors", body: "No shortcuts. Just real, soulful Filipino cooking." },
              { icon: Award, bgClass: "bg-secondary text-secondary-foreground", title: "Premium Service", body: "From plating to setup, hospitality worthy of your celebration." },
            ].map((v, i) => {
              const offset = ["", "md:translate-y-6", "", "md:translate-y-6"];
              return (
                <Reveal key={v.title} delay={i * 0.08} className={offset[i]}>
                  <div className="bg-card border border-border rounded-3xl p-7 h-full group hover:bg-foreground hover:text-background transition-all duration-500">
                    <div className={`w-14 h-14 rounded-full ${v.bgClass} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                      <v.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-display text-2xl font-semibold">{v.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground group-hover:text-background/80">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-card">
        <div className="container-eds text-center max-w-3xl mx-auto">
          <Reveal>
            <img src={LOGO_URL} alt="Logo" className="w-20 h-20 mx-auto object-contain" />
            <ScriptKicker className="block mt-4">Halika, kain tayo!</ScriptKicker>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl font-medium text-foreground">
              Let our family <span className="italic text-secondary">cater yours</span>.
            </h2>
            <p className="mt-5 text-muted-foreground text-base lg:text-lg">
              Reach out and let's craft a Filipino feast for your next big day.
            </p>
            <Link to="/contact" data-testid="about-cta-btn" className="btn-secondary mt-9">
              Reserve Your Event <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
